import React, { useMemo } from "react";
import {
  ReactFlow,
  Background,
  Controls,
  type Node,
  type Edge,
  type NodeMouseHandler,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import type {
  CapabilityMapDataset,
  CapabilityMapFilters,
  DomainMode,
  NodeKind,
  PipelineConfig,
} from "./types";
import PipelineGenericNode from "./nodes/PipelineGenericNode";

interface Props {
  dataset: CapabilityMapDataset;
  filters: CapabilityMapFilters;
  domainMode: DomainMode;
  pipelineConfig: PipelineConfig;
  onConfigChange: (config: PipelineConfig) => void;
  onNodeClick: (nodeId: string, kind: NodeKind) => void;
}

function applyFiltersToDataset(
  dataset: CapabilityMapDataset,
  filters: CapabilityMapFilters,
  domainMode: DomainMode,
): CapabilityMapDataset {
  const domainMatches = (
    domains: ("defense" | "industrial" | "dual_use")[],
  ): boolean =>
    domainMode === "all" ||
    domains.includes(domainMode as "defense" | "industrial") ||
    domains.includes("dual_use");

  let tenders = dataset.tenders.filter((t) => domainMatches(t.domains));
  if (filters.sourceTypes.length > 0) {
    tenders = tenders.filter((t) => filters.sourceTypes.includes(t.sourceType));
  }

  let systemTypes = dataset.systemTypes.filter((st) =>
    domainMatches(st.domains),
  );
  if (filters.systemTypeIds.length > 0) {
    systemTypes = systemTypes.filter((st) =>
      filters.systemTypeIds.includes(st.id),
    );
  }

  const componentTypes =
    filters.componentTypeIds.length > 0
      ? dataset.componentTypes.filter((ct) =>
          filters.componentTypeIds.includes(ct.id),
        )
      : dataset.componentTypes;

  let companies = dataset.companies.filter((c) => domainMatches(c.domains));
  if (filters.companyTypes.length > 0) {
    companies = companies.filter((c) =>
      filters.companyTypes.some((t) => c.companyTypes.includes(t)),
    );
  }

  return { ...dataset, tenders, systemTypes, componentTypes, companies };
}

// Node color palette per kind
const NODE_STYLE: Record<
  string,
  { color: string; borderColor: string; handleColor: string }
> = {
  tender: { color: "#5D4037", borderColor: "#8D6E63", handleColor: "#FFCC80" },
  system_type: {
    color: "#1565C0",
    borderColor: "#1976D2",
    handleColor: "#90CAF9",
  },
  system_capability: {
    color: "#00695C",
    borderColor: "#00897B",
    handleColor: "#80CBC4",
  },
  component_type: {
    color: "#2E7D32",
    borderColor: "#388E3C",
    handleColor: "#A5D6A7",
  },
  component_capability: {
    color: "#558B2F",
    borderColor: "#689F38",
    handleColor: "#CCFF90",
  },
  system_instance: {
    color: "#4527A0",
    borderColor: "#5E35B1",
    handleColor: "#CE93D8",
  },
  component_instance: {
    color: "#BF360C",
    borderColor: "#D84315",
    handleColor: "#FFAB91",
  },
  company: { color: "#37474F", borderColor: "#546E7A", handleColor: "#B0BEC5" },
};

const KIND_LABELS: Record<string, string> = {
  tender: "Tender",
  system_type: "System Type",
  system_capability: "System Capability",
  component_type: "Component Type",
  component_capability: "Component Capability",
  system_instance: "System Instance",
  component_instance: "Component Instance",
  company: "Company",
};

// Stable node type map (defined outside component to avoid remounting)
const NODE_TYPES = { pipelineGeneric: PipelineGenericNode };

const COL_GAP_X = 220;
const NODE_H = 72;
const NODE_GAP = 12;

function colX(col: number): number {
  return col * COL_GAP_X;
}
function rowY(row: number): number {
  return row * (NODE_H + NODE_GAP);
}

const PIPELINE_CONFIGS: Record<
  PipelineConfig,
  { label: string; description: string; colLabels: string[] }
> = {
  demand_chain: {
    label: "Demand Chain",
    description:
      "What classes of systems and components does this demand require?",
    colLabels: ["Tender", "System Type", "System Capability", "Component Type"],
  },
  supply_chain: {
    label: "Supply Chain",
    description: "What demand does this component type address?",
    colLabels: [
      "Component Type",
      "Component Capability",
      "System Capability",
      "System Type",
      "Tender",
    ],
  },
  instance_chain: {
    label: "Instance Chain",
    description: "For this specific program, who are the relevant suppliers?",
    colLabels: [
      "Tender",
      "System Instance",
      "System Capability",
      "Component Instance",
      "Company",
    ],
  },
  composition: {
    label: "Composition",
    description:
      "How system types, subsystems, and component types physically compose into each other.",
    colLabels: ["Platform / System", "Subsystem", "Component"],
  },
};

function buildDemandChain(dataset: CapabilityMapDataset): {
  nodes: Node[];
  edges: Edge[];
} {
  const nodes: Node[] = [];
  const edges: Edge[] = [];
  const addedNodes = new Set<string>();

  function addNode(
    id: string,
    kind: string,
    label: string,
    col: number,
    row: number,
    sublabel?: string,
  ) {
    if (addedNodes.has(id)) return;
    addedNodes.add(id);
    const style = NODE_STYLE[kind] ?? NODE_STYLE.company;
    nodes.push({
      id,
      type: "pipelineGeneric",
      position: { x: colX(col), y: rowY(row) },
      data: { label, sublabel, kindLabel: KIND_LABELS[kind] ?? kind, ...style },
    });
  }

  function addEdge(source: string, target: string, color: string) {
    const id = `${source}->${target}`;
    if (edges.find((e) => e.id === id)) return;
    edges.push({
      id,
      source,
      target,
      animated: true,
      style: { stroke: color, strokeWidth: 1.5 },
    });
  }

  // Col 0: Tenders
  dataset.tenders.forEach((tender, ti) => {
    addNode(
      tender.id,
      "tender",
      tender.label,
      0,
      ti,
      tender.contractingAuthority,
    );
  });

  // Col 1: System Types (via tender.systemTypeIds)
  const systemTypeRows: Record<string, number> = {};
  let stRow = 0;
  dataset.tenders.forEach((tender) => {
    tender.systemTypeIds.forEach((stId) => {
      const st = dataset.systemTypes.find((s) => s.id === stId);
      if (!st) return;
      if (!(stId in systemTypeRows)) {
        systemTypeRows[stId] = stRow++;
        addNode(stId, "system_type", st.label, 1, systemTypeRows[stId]);
      }
      addEdge(tender.id, stId, "#FFCC80");
    });
  });

  // Col 2: System Capabilities (via systemType.systemCapabilityIds)
  const scRows: Record<string, number> = {};
  let scRow = 0;
  Object.keys(systemTypeRows).forEach((stId) => {
    const st = dataset.systemTypes.find((s) => s.id === stId);
    if (!st) return;
    st.systemCapabilityIds.forEach((scId) => {
      const sc = dataset.systemCapabilities.find((c) => c.id === scId);
      if (!sc) return;
      if (!(scId in scRows)) {
        scRows[scId] = scRow++;
        addNode(scId, "system_capability", sc.label, 2, scRows[scId]);
      }
      addEdge(stId, scId, "#90CAF9");
    });
  });

  // Col 3: Component Types (via systemCapability.enabledByComponentCapIds → componentType.componentCapabilityIds)
  const ctRows: Record<string, number> = {};
  let ctRow = 0;
  Object.keys(scRows).forEach((scId) => {
    const sc = dataset.systemCapabilities.find((c) => c.id === scId);
    if (!sc) return;
    dataset.componentTypes.forEach((ct) => {
      const enables = ct.componentCapabilityIds.some((ccId) =>
        sc.enabledByComponentCapIds.includes(ccId),
      );
      if (!enables) return;
      if (!(ct.id in ctRows)) {
        ctRows[ct.id] = ctRow++;
        addNode(ct.id, "component_type", ct.label, 3, ctRows[ct.id]);
      }
      addEdge(scId, ct.id, "#80CBC4");
    });
  });

  return { nodes, edges };
}

function buildSupplyChain(dataset: CapabilityMapDataset): {
  nodes: Node[];
  edges: Edge[];
} {
  const nodes: Node[] = [];
  const edges: Edge[] = [];
  const addedNodes = new Set<string>();

  function addNode(
    id: string,
    kind: string,
    label: string,
    col: number,
    row: number,
    sublabel?: string,
  ) {
    if (addedNodes.has(id)) return;
    addedNodes.add(id);
    const style = NODE_STYLE[kind] ?? NODE_STYLE.company;
    nodes.push({
      id,
      type: "pipelineGeneric",
      position: { x: colX(col), y: rowY(row) },
      data: { label, sublabel, kindLabel: KIND_LABELS[kind] ?? kind, ...style },
    });
  }

  function addEdge(source: string, target: string, color: string) {
    const id = `${source}->${target}`;
    if (edges.find((e) => e.id === id)) return;
    edges.push({
      id,
      source,
      target,
      animated: true,
      style: { stroke: color, strokeWidth: 1.5 },
    });
  }

  // Col 0: Component Types
  dataset.componentTypes.forEach((ct, i) => {
    addNode(ct.id, "component_type", ct.label, 0, i, ct.levelHint);
  });

  // Col 1: Component Capabilities (via componentType.componentCapabilityIds)
  const ccRows: Record<string, number> = {};
  let ccRow = 0;
  dataset.componentTypes.forEach((ct) => {
    ct.componentCapabilityIds.forEach((ccId) => {
      const cc = dataset.componentCapabilities.find((c) => c.id === ccId);
      if (!cc) return;
      if (!(ccId in ccRows)) {
        ccRows[ccId] = ccRow++;
        addNode(ccId, "component_capability", cc.label, 1, ccRows[ccId]);
      }
      addEdge(ct.id, ccId, "#A5D6A7");
    });
  });

  // Col 2: System Capabilities (via componentCapability.enablesSystemCapIds)
  const scRows: Record<string, number> = {};
  let scRow = 0;
  Object.keys(ccRows).forEach((ccId) => {
    const cc = dataset.componentCapabilities.find((c) => c.id === ccId);
    if (!cc) return;
    cc.enablesSystemCapIds.forEach((scId) => {
      const sc = dataset.systemCapabilities.find((c) => c.id === scId);
      if (!sc) return;
      if (!(scId in scRows)) {
        scRows[scId] = scRow++;
        addNode(scId, "system_capability", sc.label, 2, scRows[scId]);
      }
      addEdge(ccId, scId, "#CCFF90");
    });
  });

  // Col 3: System Types (via systemType.systemCapabilityIds)
  const stRows: Record<string, number> = {};
  let stRow = 0;
  Object.keys(scRows).forEach((scId) => {
    dataset.systemTypes.forEach((st) => {
      if (!st.systemCapabilityIds.includes(scId)) return;
      if (!(st.id in stRows)) {
        stRows[st.id] = stRow++;
        addNode(st.id, "system_type", st.label, 3, stRows[st.id]);
      }
      addEdge(scId, st.id, "#80CBC4");
    });
  });

  // Col 4: Tenders (via tender.systemTypeIds)
  const tenderRows: Record<string, number> = {};
  let tRow = 0;
  Object.keys(stRows).forEach((stId) => {
    dataset.tenders.forEach((tender) => {
      if (!tender.systemTypeIds.includes(stId)) return;
      if (!(tender.id in tenderRows)) {
        tenderRows[tender.id] = tRow++;
        addNode(
          tender.id,
          "tender",
          tender.label,
          4,
          tenderRows[tender.id],
          tender.contractingAuthority,
        );
      }
      addEdge(stId, tender.id, "#FFCC80");
    });
  });

  return { nodes, edges };
}

function buildInstanceChain(dataset: CapabilityMapDataset): {
  nodes: Node[];
  edges: Edge[];
} {
  const nodes: Node[] = [];
  const edges: Edge[] = [];
  const addedNodes = new Set<string>();

  function addNode(
    id: string,
    kind: string,
    label: string,
    col: number,
    row: number,
    sublabel?: string,
  ) {
    if (addedNodes.has(id)) return;
    addedNodes.add(id);
    const style = NODE_STYLE[kind] ?? NODE_STYLE.company;
    nodes.push({
      id,
      type: "pipelineGeneric",
      position: { x: colX(col), y: rowY(row) },
      data: { label, sublabel, kindLabel: KIND_LABELS[kind] ?? kind, ...style },
    });
  }

  function addEdge(source: string, target: string, color: string) {
    const id = `${source}->${target}`;
    if (edges.find((e) => e.id === id)) return;
    edges.push({
      id,
      source,
      target,
      animated: true,
      style: { stroke: color, strokeWidth: 1.5 },
    });
  }

  // Col 0: Tenders
  dataset.tenders.forEach((tender, ti) => {
    addNode(
      tender.id,
      "tender",
      tender.label,
      0,
      ti,
      tender.contractingAuthority,
    );
  });

  // Col 1: System Instances (via tender.systemInstanceIds)
  const siRows: Record<string, number> = {};
  let siRow = 0;
  dataset.tenders.forEach((tender) => {
    tender.systemInstanceIds.forEach((siId) => {
      const si = dataset.systemInstances.find((s) => s.id === siId);
      if (!si) return;
      if (!(siId in siRows)) {
        siRows[siId] = siRow++;
        const company = si.companyId
          ? dataset.companies.find((c) => c.id === si.companyId)
          : undefined;
        addNode(
          siId,
          "system_instance",
          si.label,
          1,
          siRows[siId],
          company?.label,
        );
      }
      addEdge(tender.id, siId, "#FFCC80");
    });
  });

  // Col 2: System Capabilities (via systemInstance.systemCapabilityIds)
  const scRows: Record<string, number> = {};
  let scRow = 0;
  Object.keys(siRows).forEach((siId) => {
    const si = dataset.systemInstances.find((s) => s.id === siId);
    if (!si) return;
    si.systemCapabilityIds.forEach((scId) => {
      const sc = dataset.systemCapabilities.find((c) => c.id === scId);
      if (!sc) return;
      if (!(scId in scRows)) {
        scRows[scId] = scRow++;
        addNode(scId, "system_capability", sc.label, 2, scRows[scId]);
      }
      addEdge(siId, scId, "#CE93D8");
    });
  });

  // Col 3: Component Instances that provide enabling component capabilities
  const ciRows: Record<string, number> = {};
  let ciRow = 0;
  Object.keys(scRows).forEach((scId) => {
    const sc = dataset.systemCapabilities.find((c) => c.id === scId);
    if (!sc) return;
    dataset.componentInstances.forEach((ci) => {
      const enables = ci.componentCapabilityIds.some((ccId) =>
        sc.enabledByComponentCapIds.includes(ccId),
      );
      if (!enables) return;
      if (!(ci.id in ciRows)) {
        ciRows[ci.id] = ciRow++;
        const ct = dataset.componentTypes.find(
          (t) => t.id === ci.componentTypeId,
        );
        addNode(
          ci.id,
          "component_instance",
          ci.label,
          3,
          ciRows[ci.id],
          ct?.label,
        );
      }
      addEdge(scId, ci.id, "#80CBC4");
    });
  });

  // Col 4: Companies (via componentInstance.companyId)
  const companyRows: Record<string, number> = {};
  let companyRow = 0;
  Object.keys(ciRows).forEach((ciId) => {
    const ci = dataset.componentInstances.find((c) => c.id === ciId);
    if (!ci) return;
    const company = dataset.companies.find((c) => c.id === ci.companyId);
    if (!company) return;
    if (!(company.id in companyRows)) {
      companyRows[company.id] = companyRow++;
      addNode(
        company.id,
        "company",
        company.label,
        4,
        companyRows[company.id],
        company.companyTypes.join(", "),
      );
    }
    addEdge(ciId, company.id, "#FFAB91");
  });

  return { nodes, edges };
}

function buildComposition(dataset: CapabilityMapDataset): {
  nodes: Node[];
  edges: Edge[];
} {
  const nodes: Node[] = [];
  const edges: Edge[] = [];
  const addedNodes = new Set<string>();

  function addNode(
    id: string,
    kind: string,
    label: string,
    col: number,
    row: number,
    sublabel?: string,
  ) {
    if (addedNodes.has(id)) return;
    addedNodes.add(id);
    const style = NODE_STYLE[kind] ?? NODE_STYLE.company;
    nodes.push({
      id,
      type: "pipelineGeneric",
      position: { x: colX(col), y: rowY(row) },
      data: { label, sublabel, kindLabel: KIND_LABELS[kind] ?? kind, ...style },
    });
  }

  function addEdge(source: string, target: string, color: string) {
    const id = `${source}->${target}`;
    if (edges.find((e) => e.id === id)) return;
    edges.push({
      id,
      source,
      target,
      animated: false,
      style: { stroke: color, strokeWidth: 1.5 },
    });
  }

  // Col 0: Root SystemTypes (no parent) — "Platform / System"
  // Col 1: Child SystemTypes (with parent) + ComponentTypes with levelHint=subsystem
  // Col 2: ComponentTypes with levelHint=component (and anything more granular)
  const rootSystemTypes = dataset.systemTypes.filter((st) => !st.parentTypeId);
  const childSystemTypes = dataset.systemTypes.filter(
    (st) => !!st.parentTypeId,
  );

  const col0Rows: Record<string, number> = {};
  rootSystemTypes.forEach((st, i) => {
    col0Rows[st.id] = i;
    addNode(st.id, "system_type", st.label, 0, i);
  });

  const col1Rows: Record<string, number> = {};
  let col1Row = 0;

  childSystemTypes.forEach((st) => {
    col1Rows[st.id] = col1Row++;
    addNode(st.id, "system_type", st.label, 1, col1Rows[st.id]);
    if (st.parentTypeId && addedNodes.has(st.parentTypeId)) {
      addEdge(st.parentTypeId, st.id, "#90CAF9");
    }
  });

  const subsystemComponentIds = new Set(
    dataset.componentTypes
      .filter((ct) => ct.levelHint === "subsystem")
      .map((ct) => ct.id),
  );

  // Connect SystemTypes to their ComponentTypes; place subsystems in col 1, components in col 2
  dataset.systemTypes.forEach((st) => {
    st.componentTypeIds.forEach((ctId) => {
      const ct = dataset.componentTypes.find((c) => c.id === ctId);
      if (!ct) return;
      const targetCol = subsystemComponentIds.has(ct.id) ? 1 : 2;
      const rowsMap = targetCol === 1 ? col1Rows : null;
      if (targetCol === 1) {
        if (!(ct.id in col1Rows)) {
          col1Rows[ct.id] = col1Row++;
        }
        addNode(
          ct.id,
          "component_type",
          ct.label,
          1,
          col1Rows[ct.id],
          ct.levelHint,
        );
      } else {
        // lazy-assign row in col 2
        const existing = nodes.find((n) => n.id === ct.id);
        if (!existing) {
          const row = nodes.filter((n) => n.position.x === colX(2)).length;
          addNode(ct.id, "component_type", ct.label, 2, row, ct.levelHint);
        }
      }
      addEdge(st.id, ct.id, "#80CBC4");
      // Avoid the unused var warning
      void rowsMap;
    });
  });

  // Connect subsystem ComponentTypes to lower-level ComponentTypes that share a system capability context:
  // There is no direct component→component composition in the schema, so we stop here.

  return { nodes, edges };
}

export const CapabilityMapPipelineView: React.FC<Props> = ({
  dataset,
  filters,
  domainMode,
  pipelineConfig,
  onConfigChange,
  onNodeClick,
}) => {
  const filteredDataset = useMemo(
    () => applyFiltersToDataset(dataset, filters, domainMode),
    [dataset, filters, domainMode],
  );

  const { nodes, edges } = useMemo(() => {
    if (pipelineConfig === "demand_chain")
      return buildDemandChain(filteredDataset);
    if (pipelineConfig === "supply_chain")
      return buildSupplyChain(filteredDataset);
    if (pipelineConfig === "composition")
      return buildComposition(filteredDataset);
    return buildInstanceChain(filteredDataset);
  }, [filteredDataset, pipelineConfig]);

  const config = PIPELINE_CONFIGS[pipelineConfig];

  const kindForNode = (nodeId: string): NodeKind => {
    if (dataset.tenders.some((t) => t.id === nodeId)) return "tender";
    if (dataset.systemTypes.some((s) => s.id === nodeId)) return "system_type";
    if (dataset.systemInstances.some((s) => s.id === nodeId))
      return "system_instance";
    if (dataset.systemCapabilities.some((s) => s.id === nodeId))
      return "system_capability";
    if (dataset.componentCapabilities.some((c) => c.id === nodeId))
      return "component_capability";
    if (dataset.componentTypes.some((c) => c.id === nodeId))
      return "component_type";
    if (dataset.componentInstances.some((c) => c.id === nodeId))
      return "component_instance";
    if (dataset.companies.some((c) => c.id === nodeId)) return "company";
    return "company";
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Config selector + column labels */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 3,
          px: 2,
          py: 1,
          borderBottom: 1,
          borderColor: "divider",
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
          <FormControl size="small" sx={{ minWidth: 240 }}>
            <InputLabel sx={{ fontSize: 13 }}>Pipeline View</InputLabel>
            <Select
              value={pipelineConfig}
              label="Pipeline View"
              onChange={(e) => onConfigChange(e.target.value as PipelineConfig)}
              sx={{ fontSize: 13 }}
            >
              {(Object.keys(PIPELINE_CONFIGS) as PipelineConfig[]).map(
                (key) => (
                  <MenuItem key={key} value={key} sx={{ fontSize: 13 }}>
                    {PIPELINE_CONFIGS[key].label}
                  </MenuItem>
                ),
              )}
            </Select>
          </FormControl>
          <Typography
            variant="caption"
            sx={{ color: "text.disabled", fontSize: "0.65rem", pl: 0.5 }}
          >
            {config.description}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ flex: 1 }}>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          nodeTypes={NODE_TYPES}
          onNodeClick={
            ((_event: React.MouseEvent, node: Node) => {
              onNodeClick(node.id, kindForNode(node.id));
            }) as NodeMouseHandler
          }
          fitView
          fitViewOptions={{ padding: 0.15 }}
          colorMode="dark"
          nodesDraggable={false}
          nodesConnectable={false}
          elementsSelectable
          proOptions={{ hideAttribution: true }}
        >
          <Background color="#333" gap={20} />
          <Controls showInteractive={false} />
        </ReactFlow>
      </Box>
    </Box>
  );
};

export default CapabilityMapPipelineView;
